import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "../../api/api";
import {
  FaRunning,
  FaCalendarCheck,
  FaClipboardCheck,
  FaFileAlt,
  FaChartLine,
  FaArrowRight,
  FaCheckCircle,
  FaCampground,
  FaTshirt,
  FaClipboardList,
} from "react-icons/fa";

const CoachDashboard = () => {
  const [stats, setStats] = useState(null);
  const [coach, setCoach] = useState(null);
  const [activeCamp, setActiveCamp] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await API.camp.coachPortal.getStats();
      if (res?.data || res) {
        const data = res?.data || res;
        setStats(data.stats);
        setCoach(data.coach);
        setActiveCamp(data.active_camp);
      }
    } catch (err) {
      console.error("Error loading coach dashboard stats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-400">Loading Coach Portal...</p>
        </div>
      </div>
    );
  }

  const todayAtt = stats?.today_attendance || { total: 0, morning_present: 0, evening_present: 0 };

  return (
    <div className="space-y-6">
      {/* Signature Amber-Cyan Banner matching ManagePlayers & ManageEvents */}
      <section className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-5 sm:p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-bold mb-2 border border-amber-300">
              <FaCampground className="text-amber-600" />
              {activeCamp?.name || "Active Coaching Camp"}
            </div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">
              Coach Workspace
            </p>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-0.5 tracking-tight">
              Welcome back, Coach {coach?.name}!
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Disciplines: <span className="font-semibold text-slate-800">{coach?.disciplines?.map((d) => d.discipline_name).join(", ") || "Para Sports"}</span> &bull; {coach?.district ? `${coach.district} District` : "Andhra Pradesh"}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/coach/attendance"
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl text-xs flex items-center gap-2 shadow-sm transition-all"
            >
              <FaCalendarCheck /> Mark Today's Attendance
            </Link>
          </div>
        </div>
      </section>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Assigned Athletes */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                My Athletes
              </p>
              <h3 className="text-3xl font-bold text-slate-900 mt-1">
                {stats?.total_athletes ?? 0}
              </h3>
              <p className="text-xs text-cyan-700 font-medium mt-1">Active in this camp</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-cyan-50 border border-cyan-200 flex items-center justify-center text-cyan-700 text-2xl">
              <FaRunning />
            </div>
          </div>
        </div>

        {/* Today's Morning Attendance */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Morning Session
              </p>
              <h3 className="text-3xl font-bold text-emerald-700 mt-1">
                {todayAtt.morning_present}{" "}
                <span className="text-xs text-slate-400 font-normal">/ {stats?.total_athletes ?? 0}</span>
              </h3>
              <p className="text-xs text-emerald-600 font-medium mt-1 flex items-center gap-1">
                <FaCheckCircle className="text-[10px]" /> Present today
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-700 text-2xl">
              <FaCalendarCheck />
            </div>
          </div>
        </div>

        {/* Today's Evening Attendance */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Evening Session
              </p>
              <h3 className="text-3xl font-bold text-teal-700 mt-1">
                {todayAtt.evening_present}{" "}
                <span className="text-xs text-slate-400 font-normal">/ {stats?.total_athletes ?? 0}</span>
              </h3>
              <p className="text-xs text-teal-600 font-medium mt-1 flex items-center gap-1">
                <FaCheckCircle className="text-[10px]" /> Present today
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700 text-2xl">
              <FaCalendarCheck />
            </div>
          </div>
        </div>

        {/* Weekly & Camp Reports */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Weekly Reports
              </p>
              <h3 className="text-3xl font-bold text-amber-700 mt-1">
                {stats?.weekly_reports_count ?? 0}
              </h3>
              <p className="text-xs text-amber-600 font-medium mt-1">Submitted so far</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-700 text-2xl">
              <FaFileAlt />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Action Navigation Cards */}
      <div>
        <h2 className="text-base font-bold text-slate-900 mb-3 tracking-tight">Quick Access Hub</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/coach/athletes"
            className="group p-5 bg-white hover:bg-slate-50/80 border border-slate-200 hover:border-cyan-400 rounded-2xl transition-all shadow-sm flex items-start justify-between"
          >
            <div>
              <div className="w-10 h-10 rounded-xl bg-cyan-50 border border-cyan-200 text-cyan-700 flex items-center justify-center text-lg mb-3">
                <FaRunning />
              </div>
              <h3 className="text-sm font-bold text-slate-900 group-hover:text-cyan-800 transition-colors">
                My Athletes
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                View assigned athletes, personal records, BIB numbers, and upload missing documents.
              </p>
            </div>
            <FaArrowRight className="text-slate-400 group-hover:text-cyan-700 group-hover:translate-x-1 transition-all mt-2" />
          </Link>

          <Link
            to="/coach/attendance"
            className="group p-5 bg-white hover:bg-slate-50/80 border border-slate-200 hover:border-emerald-400 rounded-2xl transition-all shadow-sm flex items-start justify-between"
          >
            <div>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center text-lg mb-3">
                <FaCalendarCheck />
              </div>
              <h3 className="text-sm font-bold text-slate-900 group-hover:text-emerald-800 transition-colors">
                Attendance Management
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Daily Morning & Evening attendance tracking, In/Out times, and historical records.
              </p>
            </div>
            <FaArrowRight className="text-slate-400 group-hover:text-emerald-700 group-hover:translate-x-1 transition-all mt-2" />
          </Link>

          <Link
            to="/coach/report-cards"
            className="group p-5 bg-white hover:bg-slate-50/80 border border-slate-200 hover:border-sky-400 rounded-2xl transition-all shadow-sm flex items-start justify-between"
          >
            <div>
              <div className="w-10 h-10 rounded-xl bg-sky-50 border border-sky-200 text-sky-700 flex items-center justify-center text-lg mb-3">
                <FaClipboardCheck />
              </div>
              <h3 className="text-sm font-bold text-slate-900 group-hover:text-sky-800 transition-colors">
                Athlete Report Cards
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Enter weekly ratings for Endurance, Strength, Agility, Skill & Technique, and Discipline.
              </p>
            </div>
            <FaArrowRight className="text-slate-400 group-hover:text-sky-700 group-hover:translate-x-1 transition-all mt-2" />
          </Link>

          <Link
            to="/coach/weekly-reports"
            className="group p-5 bg-white hover:bg-slate-50/80 border border-slate-200 hover:border-amber-400 rounded-2xl transition-all shadow-sm flex items-start justify-between"
          >
            <div>
              <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 flex items-center justify-center text-lg mb-3">
                <FaFileAlt />
              </div>
              <h3 className="text-sm font-bold text-slate-900 group-hover:text-amber-800 transition-colors">
                Weekly Reports
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Submit Weeks 1 to 9 coaching reports with PDF attachments for each assigned athlete.
              </p>
            </div>
            <FaArrowRight className="text-slate-400 group-hover:text-amber-700 group-hover:translate-x-1 transition-all mt-2" />
          </Link>

          <Link
            to="/coach/kits"
            className="group p-5 bg-white hover:bg-slate-50/80 border border-slate-200 hover:border-teal-400 rounded-2xl transition-all shadow-sm flex items-start justify-between"
          >
            <div>
              <div className="w-10 h-10 rounded-xl bg-teal-50 border border-teal-200 text-teal-700 flex items-center justify-center text-lg mb-3">
                <FaTshirt />
              </div>
              <h3 className="text-sm font-bold text-slate-900 group-hover:text-teal-800 transition-colors">
                Kit Measurements &amp; Caste Details
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Record height, weight, dress size, shoe size, kit items, and caste/subcaste details for squad athletes.
              </p>
            </div>
            <FaArrowRight className="text-slate-400 group-hover:text-teal-700 group-hover:translate-x-1 transition-all mt-2" />
          </Link>

          <Link
            to="/coach/daily-reports"
            className="group p-5 bg-white hover:bg-slate-50/80 border border-slate-200 hover:border-emerald-400 rounded-2xl transition-all shadow-sm flex items-start justify-between"
          >
            <div>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center text-lg mb-3">
                <FaClipboardList />
              </div>
              <h3 className="text-sm font-bold text-slate-900 group-hover:text-emerald-800 transition-colors">
                Daily Athlete Reports
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Log daily individual athlete training sessions, drills, fitness scores, discipline, and upload sheets.
              </p>
            </div>
            <FaArrowRight className="text-slate-400 group-hover:text-emerald-700 group-hover:translate-x-1 transition-all mt-2" />
          </Link>

          <Link
            to="/coach/camp-reports"
            className="group p-5 bg-white hover:bg-slate-50/80 border border-slate-200 hover:border-purple-400 rounded-2xl transition-all shadow-sm flex items-start justify-between"
          >
            <div>
              <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-200 text-purple-700 flex items-center justify-center text-lg mb-3">
                <FaChartLine />
              </div>
              <h3 className="text-sm font-bold text-slate-900 group-hover:text-purple-800 transition-colors">
                Week 1 vs Week 9 Camp Reports
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Record initial & final performance assessments, height measurements, and upload official reports.
              </p>
            </div>
            <FaArrowRight className="text-slate-400 group-hover:text-purple-700 group-hover:translate-x-1 transition-all mt-2" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CoachDashboard;
